import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProductType } from '@/model/draft/getProductType';
import { getSelectedSizes } from '@/model/draft/getSelectedSizes';
import { getDiscountBreakdown } from '@/model/pricing/getDiscountBreakdown';
import { initBasketAction } from '@/redux/basket/actions/initBasketAction';
import { selectBasketId } from '@/redux/basket/getters/selectBasketId';
import { selectBasketItems } from '@/redux/basket/getters/selectBasketItems';
import { selectPriceBreakdown } from '@/redux/common/getters/selectPriceBreakdown';
import { getCurrency } from '@/redux/core/getters/getCurrency';
import { fetchDiscountsSupported } from '@/redux/discounts/actions/fetchDiscountsSupported';
import { initProductTypeDiscounts } from '@/redux/discounts/actions/initProductTypeDiscounts';
import { getBasketItemsSupportingDiscountCount } from '@/redux/discounts/getters/getBasketItemsSupportingDiscountCount';
import { selectProductTypeDiscounts } from '@/redux/discounts/getters/selectProductTypeDiscounts';
import { getDraft } from '@/redux/draft/getters/getDraft';
import { StoreDispatch } from '@/redux/types';

export const usePriceDetails = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const draft = useSelector(getDraft);
  const productType = getProductType(draft);
  const discounts = useSelector(selectProductTypeDiscounts(productType.id));
  const priceBreakdown = useSelector(selectPriceBreakdown);
  const basketId = useSelector(selectBasketId);
  const basketItems = useSelector(selectBasketItems);
  const basketItemCount = useSelector(getBasketItemsSupportingDiscountCount);
  const currency = useSelector(getCurrency);
  const selectedSizes = getSelectedSizes(draft);
  const selectedSizesItemCount = Object.values(selectedSizes).reduce(
    (total, count) => total + count,
    0
  );
  const totalItemCount = basketItemCount + selectedSizesItemCount;

  useEffect(() => {
    dispatch(initBasketAction(basketId));
  }, [basketId, dispatch]);

  useEffect(() => {
    dispatch(fetchDiscountsSupported(basketItems));
  }, [basketItems, dispatch]);

  useEffect(() => {
    dispatch(initProductTypeDiscounts(productType.id));
  }, [productType, dispatch]);

  const discountBreakdown = getDiscountBreakdown(
    priceBreakdown.product,
    totalItemCount,
    selectedSizesItemCount,
    discounts,
    currency,
    productType
  );

  return [
    productType,
    discountBreakdown,
    selectedSizes,
    selectedSizesItemCount,
    totalItemCount,
    priceBreakdown,
  ] as const;
};
